<template>
  <b-sidebar
    id="sidebar-axis-body-cam-system-controller"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0 text-capitalize">
          {{ $t("actions.edit") }} {{ itemEdit.name || itemEdit.serial_number }}
        </h5>
        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <b-form class="p-2" @submit.prevent="handleSubmit(onSubmit)" @reset.prevent="resetForm">
          <!-- Name -->
          <validation-provider #default="validationContext" name="name">
            <b-form-group
              class="text-capitalize"
              :label="$t('axis_body_cam.system_controllers.name')"
              label-for="name"
            >
              <b-form-input
                id="name"
                v-model="itemData.name"
                :state="getValidationState(validationContext)"
                trim
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Units -->
          <validation-provider #default="validationContext" name="Unit">
            <b-form-group
              class="text-capitalize"
              :label="$tc('unit.title', 2)"
              label-for="id_unit"
              :state="getValidationState(validationContext)"
            >
              <select-paginate
                v-model="itemData.id_unit"
                :items="unitOptions"
                input-id="id_unit"
                :multiple="true"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Proxy RTSP Server -->
          <validation-provider #default="validationContext" name="ProxyRtspServer">
            <b-form-group
              class="text-capitalize"
              :label="$t('axis_body_cam.system_controllers.proxy_rtsp_server')"
              label-for="id_proxy_rtsp_server"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.id_proxy_rtsp_server"
                :options="proxyRtspServerOptions"
                :reduce="(val) => val.value"
                :clearable="true"
                input-id="id_proxy_rtsp_server"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Users: they inherit every camera of this dock -->
          <validation-provider #default="validationContext" name="Users">
            <b-form-group
              class="text-capitalize"
              :label="$t('axis_body_cam.system_controllers.users')"
              label-for="id_user"
              :state="getValidationState(validationContext)"
            >
              <select-paginate
                v-model="itemData.id_user"
                :items="userOptions"
                input-id="id_user"
                :multiple="true"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Groups: their users inherit every camera of this dock -->
          <validation-provider #default="validationContext" name="Groups">
            <b-form-group
              class="text-capitalize"
              :label="$t('axis_body_cam.system_controllers.groups')"
              label-for="id_group"
              :state="getValidationState(validationContext)"
            >
              <select-paginate
                v-model="itemData.id_group"
                :items="groupOptions"
                input-id="id_group"
                :multiple="true"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button variant="primary" class="mr-2" type="submit">
              {{ $t("actions.accept") }}
            </b-button>
            <b-button type="button" variant="outline-secondary" @click="hide">
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import { BSidebar, BForm, BFormGroup, BFormInput, BFormInvalidFeedback, BButton } from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { ref } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import store from "@/store";
import SelectPaginate from "@/views/components/SelectPaginate.vue";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    SelectPaginate,
    ValidationProvider,
    ValidationObserver,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    unitOptions: {
      type: Array,
      required: true,
    },
    proxyRtspServerOptions: {
      type: Array,
      required: true,
    },
    userOptions: {
      type: Array,
      required: true,
    },
    groupOptions: {
      type: Array,
      required: true,
    },
  },
  setup(props, { emit }) {
    const toast = useToast();

    const blankData = {
      name: "",
      id_unit: [],
      id_user: [],
      id_group: [],
      id_proxy_rtsp_server: null,
    };

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));

    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
    };

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetData);

    const loadData = () => {
      itemData.value.name = props.itemEdit.name || "";
      itemData.value.id_unit = (props.itemEdit.units || []).map((unit) => ({
        value: unit.id,
        label: unit.description,
      }));
      itemData.value.id_user = (props.itemEdit.users || []).map((user) => ({
        value: user.id,
        label: user.email,
      }));
      itemData.value.id_group = (props.itemEdit.groups || []).map((group) => ({
        value: group.id,
        label: group.name,
      }));
      itemData.value.id_proxy_rtsp_server = props.itemEdit.id_proxy_rtsp_server ?? null;
    };

    const onSubmit = () => {
      const params = {
        name: itemData.value.name || null,
        id_unit: itemData.value.id_unit.map((unit) => unit.value),
        id_user: itemData.value.id_user.map((user) => user.value),
        id_group: itemData.value.id_group.map((group) => group.value),
        id_proxy_rtsp_server: itemData.value.id_proxy_rtsp_server,
      };

      store
        .dispatch("axis_body_cam_system_controllers/edit", { id: props.itemEdit.id, params })
        .then((response) => {
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: { title: response.message, icon: "CheckIcon", variant: "success" },
            });
            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: { title: response.message, icon: "AlertTriangleIcon", variant: "danger" },
            });
          }
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response?.data?.message || "Error",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    return {
      itemData,
      refFormObserver,
      getValidationState,
      resetForm,
      loadData,
      onSubmit,
    };
  },
};
</script>
